// features/pricing/viewModel/usePricingViewModel.ts
import { useReducer, useCallback, useEffect } from "react";
import {
  PricingState,
  initialPricingState,
  pricingReducer,
  PricingActionType,
  fetchStart,
  fetchSuccess,
  fetchError,
  fetchCurrentPlanSuccess,
  resetPricing,
} from "../model/PricingState";
import {
  CreateCheckoutSessionPayload,
  transformPricingPlan,
} from "../model/PricingModel";
import pricingApi, {
  downgradePlan,
  getBillingManagementLink,
  getCurrentPlan,
  upgradePlan,
  switchBillingPeriod,
} from "../api/pricingApi";
import apiService from "../../../services/api";

const usePricingViewModel = () => {
  const [state, dispatch] = useReducer(pricingReducer, initialPricingState);

  const fetchPricingPlans = useCallback(async () => {
    try {
      dispatch(fetchStart());

      const response = await pricingApi.getPricingPlans();

      if (response.success && Array.isArray(response.plans)) {
        const transformedPlans = response.plans.map(transformPricingPlan);
        dispatch(fetchSuccess(transformedPlans));
      } else {
        throw new Error("Invalid response format from server");
      }
    } catch (error: unknown) {
      console.error("Error fetching pricing plans:", error);

      const errorMessage =
        error instanceof Error ? error.message : "Failed to load pricing plans";

      dispatch(fetchError(errorMessage));
    }
  }, []);

  const createCheckoutSession = async (
    payload: CreateCheckoutSessionPayload,
  ) => {
    try {
      const response = await pricingApi.createCheckoutSession(payload);
      return response;
    } catch (error) {
      console.error("Error creating checkout session:", error);
      throw error;
    }
  };
  const fetchCurrentPlan = useCallback(async () => {
    if (!apiService.isAuthenticated()) return;
    try {
      const response = await getCurrentPlan();
      dispatch(fetchCurrentPlanSuccess(response.data));
      return response.data;
    } catch (error) {
      console.error("Error fetching current plan:", error);
      throw error;
    }
  }, []);
  const handleManageBilling = async () => {
    try {
      const response = await getBillingManagementLink(
        `${window.location.origin}/company/flowsetting`,
      );
      const portalUrl = response.data?.portal_url;
      if (portalUrl) {
        window.location.href = portalUrl;
      }
    } catch (error) {
      console.error("Error managing billing:", error);
    }
  };

  const handleDowngradePlan = async (
    newTierId: string,
    billingPeriod: string,
  ) => {
    try {
      const payload = {
        company_id: state.currentPlan?.company_id ?? "",
        new_tier_id: newTierId,
        billing_period: billingPeriod,
      };

      const response = await downgradePlan(payload);
      console.log("Downgrade response:", response);
      await fetchCurrentPlan(); // Refresh current plan after downgrade
      return response;
    } catch (error) {
      console.error("Error downgrading plan:", error);
      throw error;
    }
  };
  const handleUpgradePlan = async (
    newTierId: string,
    billingPeriod: string,
  ) => {
    try {
      const payload = {
        company_id: state.currentPlan?.company_id ?? "",
        new_tier_id: newTierId,
        billing_period: billingPeriod,
      };

      const response = await upgradePlan(payload);
      console.log("Upgrade response:", response);
      await fetchCurrentPlan();
      return response;
    } catch (error) {
      console.error("Error upgrading plan:", error);
      throw error;
    }
  };

  const handleSwitchBillingPeriod = async (
    currentTierId: string,
    billingPeriod: string,
  ) => {
    try {
      const payload = {
        company_id: state.currentPlan?.company_id ?? "",
        current_tier_id: currentTierId,
        billing_period: billingPeriod,
      };

      const response = await switchBillingPeriod(payload);
      await fetchCurrentPlan();
      return response;
    } catch (error) {
      console.error("Error switching billing period:", error);
      throw error;
    }
  };

  // Auto-fetch on mount
  useEffect(() => {
    fetchPricingPlans();
    if (apiService.isAuthenticated()) {
      fetchCurrentPlan();
    }
  }, [fetchPricingPlans, fetchCurrentPlan]);

  const retry = () => {
    fetchPricingPlans();
  };

  const reset = () => {
    dispatch(resetPricing());
  };

  return {
    // State
    plans: state.plans,
    isLoading: state.isLoading,
    error: state.error,

    // Actions
    fetchPricingPlans,
    handleManageBilling,
    handleDowngradePlan,
    handleUpgradePlan,
    handleSwitchBillingPeriod,
    retry,
    reset,
    createCheckoutSession,
    // State object (for debugging or if needed elsewhere)
    state,

    // Dispatch (exposed if needed for more complex actions)
    dispatch,
    fetchCurrentPlan,
  };
};

export default usePricingViewModel;
